/**
 * 🌙 NYX 2.0 - LES ZONES D'OMBRE PROFONDES
 * 
 * "Au-delà des péchés... les vraies profondeurs de l'âme"
 * 
 * 5 DIMENSIONS DE L'OMBRE :
 * 
 * 1. 🔗 STYLE D'ATTACHEMENT (Bowlby/Ainsworth)
 *    - Sécure, Anxieux, Évitant, Désorganisé
 *    → Matching: éviter les couples toxiques
 * 
 * 2. 🩹 LES 5 BLESSURES (Lise Bourbeau)
 *    - Rejet, Abandon, Humiliation, Trahison, Injustice
 *    → Matching: trouver des "guérisseurs" compatibles
 * 
 * 3. 🎭 SHADOW SELF (Jung)
 *    - Ce qui t'irrite = ce que tu n'acceptes pas en toi
 *    → Matching: miroirs ou complémentaires ?
 * 
 * 4. 🌊 INTENSITÉ ÉMOTIONNELLE
 *    - Besoin de stabilité vs tolérance au chaos
 *    → Matching: équilibre émotionnel
 * 
 * 5. 🚫 DEAL-BREAKERS SECRETS
 *    - Les vrais critères non-négociables
 *    → Matching: filtrage intelligent
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Shield, Eye, Flame, Moon, Star, 
  Lock, Unlock, AlertTriangle, Check, X,
  ChevronRight, ChevronLeft, Sparkles, Brain,
  Link, Unlink, HeartCrack, Mask, Waves, Ban
} from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// ============================================
// 1. 🔗 STYLES D'ATTACHEMENT
// ============================================

export const ATTACHMENT_STYLES = {
  secure: {
    id: 'secure',
    name: 'Sécure',
    emoji: '🛡️',
    color: 'from-green-500 to-emerald-600',
    description: 'Tu te sens à l\'aise dans l\'intimité et l\'indépendance',
    traits: ['Confiant(e)', 'Équilibré(e)', 'Communicatif(ve)', 'Stable'],
    inRelationship: 'Tu donnes de l\'espace tout en restant connecté(e)',
    fear: 'Peu de peurs relationnelles majeures',
    compatibleWith: ['secure', 'anxious', 'avoidant'], // Peut stabiliser les autres
    healing: true // Peut "guérir" les autres styles
  },
  anxious: {
    id: 'anxious',
    name: 'Anxieux',
    emoji: '💗',
    color: 'from-pink-500 to-rose-600',
    description: 'Tu as besoin de beaucoup de réassurance et de proximité',
    traits: ['Intense', 'Dévoué(e)', 'Sensible', 'Expressif(ve)'],
    inRelationship: 'Tu cherches constamment des signes d\'amour',
    fear: 'Peur de l\'abandon, besoin de validation',
    compatibleWith: ['secure'], // ⚠️ Éviter anxieux+évitant
    warning: ['avoidant'] // Combinaison toxique
  },
  avoidant: {
    id: 'avoidant',
    name: 'Évitant',
    emoji: '🏔️',
    color: 'from-blue-500 to-indigo-600',
    description: 'Tu valorises ton indépendance et gardes une distance émotionnelle',
    traits: ['Indépendant(e)', 'Autonome', 'Réservé(e)', 'Libre'],
    inRelationship: 'Tu as besoin d\'espace et de liberté',
    fear: 'Peur de l\'engulfment, de perdre son identité',
    compatibleWith: ['secure', 'avoidant'],
    warning: ['anxious'] // Combinaison toxique
  },
  disorganized: {
    id: 'disorganized',
    name: 'Désorganisé',
    emoji: '🌪️',
    color: 'from-purple-500 to-violet-600',
    description: 'Tu oscilles entre désir de proximité et peur de l\'intimité',
    traits: ['Complexe', 'Imprévisible', 'Profond(e)', 'Intense'],
    inRelationship: 'Push-pull : tu attires puis repousses',
    fear: 'Peur simultanée de l\'abandon ET de l\'intimité',
    compatibleWith: ['secure'], // A besoin de stabilité
    needsHealing: true
  }
};

// Questions pour identifier le style d'attachement
export const ATTACHMENT_QUESTIONS = [
  {
    id: 'att_1',
    question: 'Quand ton/ta partenaire ne répond pas à tes messages pendant plusieurs heures...',
    options: [
      { id: 'secure', text: 'Tu te dis qu\'il/elle est occupé(e) et tu continues ta journée', score: { secure: 3 } },
      { id: 'anxious', text: 'Tu commences à t\'inquiéter et tu vérifies ton téléphone souvent', score: { anxious: 3 } },
      { id: 'avoidant', text: 'Tu apprécies ce moment de liberté', score: { avoidant: 3 } },
      { id: 'disorganized', text: 'Tu oscilles entre inquiétude et envie de ne plus y penser', score: { disorganized: 3 } }
    ]
  },
  {
    id: 'att_2',
    question: 'Dans une relation, tu préfères...',
    options: [
      { id: 'secure', text: 'Un équilibre entre moments ensemble et indépendance', score: { secure: 3 } },
      { id: 'anxious', text: 'Passer le maximum de temps ensemble', score: { anxious: 3 } },
      { id: 'avoidant', text: 'Garder une bonne partie de ta vie séparée', score: { avoidant: 3 } },
      { id: 'disorganized', text: 'Ça dépend des jours, tu changes souvent d\'avis', score: { disorganized: 3 } }
    ]
  },
  {
    id: 'att_3',
    question: 'Quand quelqu\'un te dit "je t\'aime" pour la première fois...',
    options: [
      { id: 'secure', text: 'Tu réponds naturellement si tu le ressens aussi', score: { secure: 3 } },
      { id: 'anxious', text: 'Tu es soulagé(e) et tu le dis peut-être avant eux', score: { anxious: 3 } },
      { id: 'avoidant', text: 'Tu te sens un peu piégé(e) ou mal à l\'aise', score: { avoidant: 3 } },
      { id: 'disorganized', text: 'Tu ressens de la joie mélangée à de la panique', score: { disorganized: 3 } }
    ]
  },
  {
    id: 'att_4',
    question: 'Après une dispute avec ton/ta partenaire...',
    options: [
      { id: 'secure', text: 'Tu cherches à discuter calmement pour résoudre le problème', score: { secure: 3 } },
      { id: 'anxious', text: 'Tu as besoin de te réconcilier immédiatement', score: { anxious: 3 } },
      { id: 'avoidant', text: 'Tu préfères prendre de la distance pour réfléchir', score: { avoidant: 3 } },
      { id: 'disorganized', text: 'Tu ne sais pas si tu veux te rapprocher ou fuir', score: { disorganized: 3 } }
    ]
  }
];

// ============================================
// 2. 🩹 LES 5 BLESSURES (Lise Bourbeau)
// ============================================

export const FIVE_WOUNDS = {
  rejection: {
    id: 'rejection',
    name: 'Rejet',
    emoji: '🚪',
    color: 'from-gray-500 to-slate-600',
    description: 'La peur de ne pas avoir le droit d\'exister',
    mask: 'Le Fuyant',
    behavior: 'Tu te fais petit(e), tu t\'effaces',
    healing: 'Être accepté(e) tel(le) que tu es',
    healingPartner: ['abandonment', 'humiliation'] // Quelqu'un qui a peur de perdre
  },
  abandonment: {
    id: 'abandonment',
    name: 'Abandon',
    emoji: '🌧️',
    color: 'from-blue-500 to-cyan-600',
    description: 'La peur d\'être seul(e), laissé(e) pour compte',
    mask: 'Le Dépendant',
    behavior: 'Tu as besoin de présence constante',
    healing: 'Être choisi(e) chaque jour',
    healingPartner: ['rejection'] // Quelqu'un qui a peur de ne pas être vu
  },
  humiliation: {
    id: 'humiliation',
    name: 'Humiliation',
    emoji: '😔',
    color: 'from-amber-500 to-orange-600',
    description: 'La peur d\'être jugé(e), ridiculisé(e)',
    mask: 'Le Masochiste',
    behavior: 'Tu fais passer les autres avant toi',
    healing: 'Être respecté(e) et valorisé(e)',
    healingPartner: ['injustice'] // Quelqu'un qui défend l'équité
  },
  betrayal: {
    id: 'betrayal',
    name: 'Trahison',
    emoji: '🗡️',
    color: 'from-red-500 to-rose-600',
    description: 'La peur de ne pas pouvoir faire confiance',
    mask: 'Le Contrôlant',
    behavior: 'Tu veux tout contrôler pour éviter les surprises',
    healing: 'Pouvoir faire confiance aveuglément',
    healingPartner: ['abandonment'] // Quelqu'un de fidèle par peur de perdre
  },
  injustice: {
    id: 'injustice',
    name: 'Injustice',
    emoji: '⚖️',
    color: 'from-violet-500 to-purple-600',
    description: 'La peur de ne pas être traité(e) équitablement',
    mask: 'Le Rigide',
    behavior: 'Tu es perfectionniste, exigeant(e)',
    healing: 'Être reconnu(e) pour ta valeur',
    healingPartner: ['humiliation'] // Quelqu'un qui valorise les autres
  }
};

// Questions pour identifier la blessure dominante
export const WOUND_QUESTIONS = [
  {
    id: 'wound_1',
    question: 'Quand tu étais enfant, qu\'est-ce qui te faisait le plus mal ?',
    options: [
      { id: 'rejection', text: 'Avoir l\'impression de ne pas être désiré(e)', score: { rejection: 3 } },
      { id: 'abandonment', text: 'Quand tes parents étaient absents ou partaient', score: { abandonment: 3 } },
      { id: 'humiliation', text: 'Être moqué(e) ou critiqué(e) devant les autres', score: { humiliation: 3 } },
      { id: 'betrayal', text: 'Quand on ne tenait pas une promesse', score: { betrayal: 3 } },
      { id: 'injustice', text: 'Être puni(e) alors que ce n\'était pas ta faute', score: { injustice: 3 } }
    ]
  },
  {
    id: 'wound_2',
    question: 'Dans une relation amoureuse, ta plus grande peur serait...',
    options: [
      { id: 'rejection', text: 'Qu\'on ne me veuille pas vraiment', score: { rejection: 3 } },
      { id: 'abandonment', text: 'Qu\'on me quitte du jour au lendemain', score: { abandonment: 3 } },
      { id: 'humiliation', text: 'Qu\'on me ridiculise devant les autres', score: { humiliation: 3 } },
      { id: 'betrayal', text: 'Qu\'on me trompe ou me mente', score: { betrayal: 3 } },
      { id: 'injustice', text: 'Qu\'on ne reconnaisse pas mes efforts', score: { injustice: 3 } }
    ]
  },
  {
    id: 'wound_3',
    question: 'Face à un conflit, tu as tendance à...',
    options: [
      { id: 'rejection', text: 'Te retirer, disparaître pour éviter le rejet', score: { rejection: 3 } },
      { id: 'abandonment', text: 'Tout faire pour garder la paix, même céder', score: { abandonment: 3 } },
      { id: 'humiliation', text: 'Prendre sur toi même si ce n\'est pas ta faute', score: { humiliation: 3 } },
      { id: 'betrayal', text: 'Vouloir tout contrôler, avoir le dernier mot', score: { betrayal: 3 } },
      { id: 'injustice', text: 'Argumenter pour prouver que tu as raison', score: { injustice: 3 } }
    ]
  }
];

// ============================================
// 3. 🎭 SHADOW SELF (Jung)
// ============================================

export const SHADOW_TRAITS = {
  chaos: {
    id: 'chaos',
    name: 'Le Chaos',
    emoji: '🌪️',
    irritatedBy: 'Les gens imprévisibles, instables',
    hiddenSelf: 'Tu refoules ton propre désir de liberté et de spontanéité',
    integration: 'Accepter que l\'ordre parfait n\'existe pas'
  },
  control: {
    id: 'control',
    name: 'Le Contrôle',
    emoji: '🎛️',
    irritatedBy: 'Les gens qui veulent tout contrôler',
    hiddenSelf: 'Tu refoules ton propre besoin de structure et de sécurité',
    integration: 'Accepter ton besoin de maîtrise'
  },
  weakness: {
    id: 'weakness',
    name: 'La Faiblesse',
    emoji: '💧',
    irritatedBy: 'Les gens trop émotifs ou "faibles"',
    hiddenSelf: 'Tu refoules ta propre vulnérabilité',
    integration: 'Accepter que la vulnérabilité est une force'
  },
  arrogance: {
    id: 'arrogance',
    name: 'L\'Arrogance',
    emoji: '👑',
    irritatedBy: 'Les gens arrogants ou prétentieux',
    hiddenSelf: 'Tu refoules ta propre fierté et tes ambitions',
    integration: 'Accepter que tu mérites de briller aussi'
  },
  neediness: {
    id: 'neediness',
    name: 'La Dépendance',
    emoji: '🔗',
    irritatedBy: 'Les gens "collants" ou dépendants',
    hiddenSelf: 'Tu refoules ton propre besoin d\'amour et de connexion',
    integration: 'Accepter que tu as besoin des autres'
  },
  coldness: {
    id: 'coldness',
    name: 'La Froideur',
    emoji: '❄️',
    irritatedBy: 'Les gens froids et distants',
    hiddenSelf: 'Tu refoules ta propre capacité à te protéger',
    integration: 'Accepter que parfois, la distance est nécessaire'
  }
};

// Questions pour identifier le Shadow Self
export const SHADOW_QUESTIONS = [
  {
    id: 'shadow_1',
    question: 'Quel type de personne t\'irrite le plus en amour ?',
    options: [
      { id: 'chaos', text: 'Les gens qui ne savent jamais ce qu\'ils veulent', score: { chaos: 3 } },
      { id: 'control', text: 'Les gens qui veulent tout décider', score: { control: 3 } },
      { id: 'weakness', text: 'Les gens qui pleurent pour un rien', score: { weakness: 3 } },
      { id: 'arrogance', text: 'Les gens qui se croient supérieurs', score: { arrogance: 3 } },
      { id: 'neediness', text: 'Les gens qui ne peuvent pas être seuls', score: { neediness: 3 } },
      { id: 'coldness', text: 'Les gens qui ne montrent jamais leurs émotions', score: { coldness: 3 } }
    ]
  },
  {
    id: 'shadow_2',
    question: 'Si tu devais choisir un défaut que tu ne supportes PAS chez un(e) partenaire...',
    options: [
      { id: 'chaos', text: 'L\'imprévisibilité constante', score: { chaos: 3 } },
      { id: 'control', text: 'Le besoin de tout contrôler', score: { control: 3 } },
      { id: 'weakness', text: 'La fragilité émotionnelle excessive', score: { weakness: 3 } },
      { id: 'arrogance', text: 'L\'ego surdimensionné', score: { arrogance: 3 } },
      { id: 'neediness', text: 'Le besoin constant de validation', score: { neediness: 3 } },
      { id: 'coldness', text: 'L\'incapacité à montrer de l\'affection', score: { coldness: 3 } }
    ]
  }
];

// ============================================
// 4. 🌊 INTENSITÉ ÉMOTIONNELLE
// ============================================

export const EMOTIONAL_INTENSITY = {
  calm: {
    id: 'calm',
    name: 'Mer Calme',
    emoji: '🏖️',
    level: 1,
    description: 'Tu cherches la stabilité et la sérénité',
    inLove: 'Amour tranquille, tendre, prévisible',
    compatible: ['calm', 'moderate']
  },
  moderate: {
    id: 'moderate',
    name: 'Vagues Douces',
    emoji: '🌊',
    level: 2,
    description: 'Tu aimes les émotions mais avec équilibre',
    inLove: 'Passion mesurée, moments intenses mais stables',
    compatible: ['calm', 'moderate', 'intense']
  },
  intense: {
    id: 'intense',
    name: 'Tempête',
    emoji: '⛈️',
    level: 3,
    description: 'Tu vis tout à 100%, les hauts et les bas',
    inLove: 'Passion dévorante, montagnes russes émotionnelles',
    compatible: ['moderate', 'intense', 'chaotic']
  },
  chaotic: {
    id: 'chaotic',
    name: 'Tsunami',
    emoji: '🌪️',
    level: 4,
    description: 'Tu es attiré(e) par le chaos et l\'intensité extrême',
    inLove: 'Relation explosive, "nous contre le monde"',
    compatible: ['intense', 'chaotic']
  }
};

// Questions pour l'intensité émotionnelle
export const INTENSITY_QUESTIONS = [
  {
    id: 'int_1',
    question: 'Ta relation idéale ressemblerait à...',
    options: [
      { id: 'calm', text: 'Un lac paisible où l\'on se baigne ensemble', score: { calm: 3 } },
      { id: 'moderate', text: 'Une rivière avec quelques rapides excitants', score: { moderate: 3 } },
      { id: 'intense', text: 'L\'océan avec ses tempêtes et ses accalmies', score: { intense: 3 } },
      { id: 'chaotic', text: 'Un tsunami qui emporte tout sur son passage', score: { chaotic: 3 } }
    ]
  },
  {
    id: 'int_2',
    question: 'Après combien de temps sans disputes commences-tu à t\'ennuyer ?',
    options: [
      { id: 'calm', text: 'Jamais, j\'adore la paix', score: { calm: 3 } },
      { id: 'moderate', text: 'Quelques semaines, une petite tension est saine', score: { moderate: 3 } },
      { id: 'intense', text: 'Quelques jours, j\'ai besoin de passion', score: { intense: 3 } },
      { id: 'chaotic', text: 'Dès le lendemain, le calme me stresse', score: { chaotic: 3 } }
    ]
  }
];

// ============================================
// 5. 🚫 DEAL-BREAKERS SECRETS
// ============================================

export const DEAL_BREAKERS = [
  { id: 'db_jealousy', text: 'La jalousie excessive', category: 'behavior' },
  { id: 'db_lies', text: 'Les mensonges, même petits', category: 'trust' },
  { id: 'db_no_ambition', text: 'Le manque d\'ambition', category: 'lifestyle' },
  { id: 'db_too_social', text: 'Trop de sorties/fêtes', category: 'lifestyle' },
  { id: 'db_not_social', text: 'Pas assez de vie sociale', category: 'lifestyle' },
  { id: 'db_no_humor', text: 'Le manque d\'humour', category: 'personality' },
  { id: 'db_clingy', text: 'Le besoin constant d\'attention', category: 'behavior' },
  { id: 'db_distant', text: 'La froideur émotionnelle', category: 'behavior' },
  { id: 'db_no_passion', text: 'Le manque de passion physique', category: 'intimacy' },
  { id: 'db_too_fast', text: 'Vouloir aller trop vite', category: 'rhythm' },
  { id: 'db_too_slow', text: 'Ne jamais vouloir s\'engager', category: 'rhythm' },
  { id: 'db_kids', text: 'Ne pas vouloir d\'enfants', category: 'life_goals' },
  { id: 'db_no_kids', text: 'Vouloir absolument des enfants', category: 'life_goals' },
  { id: 'db_religion', text: 'Incompatibilité religieuse/spirituelle', category: 'values' },
  { id: 'db_politics', text: 'Incompatibilité politique', category: 'values' },
  { id: 'db_money', text: 'Rapport malsain à l\'argent', category: 'lifestyle' },
  { id: 'db_hygiene', text: 'Le manque d\'hygiène', category: 'basic' },
  { id: 'db_rudeness', text: 'Être impoli avec les serveurs/inconnus', category: 'behavior' }
];

// ============================================
// 🔮 CALCUL DU SCORE D'OMBRE
// ============================================

export const calculateShadowScore = (profile1, profile2) => {
  let score = 0;
  let maxScore = 0;
  const insights = [];

  // 1. Compatibilité d'attachement (30 points max)
  maxScore += 30;
  const att1 = profile1.attachmentStyle;
  const att2 = profile2.attachmentStyle;
  
  if (att1 && att2) {
    const style1 = ATTACHMENT_STYLES[att1];
    const style2 = ATTACHMENT_STYLES[att2];
    
    // Check si combinaison toxique
    if (style1?.warning?.includes(att2) || style2?.warning?.includes(att1)) {
      score += 5; // Faible compatibilité
      insights.push({
        type: 'warning',
        emoji: '⚠️',
        text: 'Attention: vos styles d\'attachement peuvent créer des tensions'
      });
    } else if (style1?.compatibleWith?.includes(att2)) {
      score += 30;
      if (style1?.healing || style2?.healing) {
        insights.push({
          type: 'positive',
          emoji: '💚',
          text: 'Vous pouvez vous apporter de la stabilité mutuelle'
        });
      }
    } else {
      score += 15;
    }
  }

  // 2. Compatibilité des blessures (25 points max)
  maxScore += 25;
  const wound1 = profile1.dominantWound;
  const wound2 = profile2.dominantWound;
  
  if (wound1 && wound2) {
    const w1 = FIVE_WOUNDS[wound1];
    const w2 = FIVE_WOUNDS[wound2];
    
    // Blessures complémentaires = guérison potentielle
    if (w1?.healingPartner?.includes(wound2) || w2?.healingPartner?.includes(wound1)) {
      score += 25;
      insights.push({
        type: 'healing',
        emoji: '🩹',
        text: 'Vos blessures peuvent se guérir mutuellement'
      });
    } else if (wound1 === wound2) {
      score += 15; // Même blessure = compréhension mais risque de résonance négative
      insights.push({
        type: 'mirror',
        emoji: '🪞',
        text: 'Vous partagez la même blessure - compréhension profonde mais attention aux déclencheurs'
      });
    } else {
      score += 18;
    }
  }

  // 3. Shadow Self - Miroir ou complément (20 points max)
  maxScore += 20;
  const shadow1 = profile1.shadowTrait;
  const shadow2 = profile2.shadowTrait;
  
  if (shadow1 && shadow2) {
    if (shadow1 === shadow2) {
      score += 10; // Même ombre = miroir, peut être confrontant
      insights.push({
        type: 'mirror',
        emoji: '🎭',
        text: 'Vous avez la même ombre - miroir révélateur'
      });
    } else {
      score += 20; // Différent = complémentaire
    }
  }

  // 4. Intensité émotionnelle (15 points max)
  maxScore += 15;
  const int1 = profile1.emotionalIntensity;
  const int2 = profile2.emotionalIntensity;
  
  if (int1 && int2) {
    const i1 = EMOTIONAL_INTENSITY[int1];
    const i2 = EMOTIONAL_INTENSITY[int2];
    
    if (i1?.compatible?.includes(int2)) {
      score += 15;
    } else {
      const diff = Math.abs((i1?.level || 2) - (i2?.level || 2));
      score += Math.max(5, 15 - diff * 5);
      if (diff >= 2) {
        insights.push({
          type: 'warning',
          emoji: '🌊',
          text: 'Différence d\'intensité émotionnelle - l\'un pourrait être submergé, l\'autre frustré'
        });
      }
    }
  }

  // 5. Deal-breakers (10 points max)
  maxScore += 10;
  const db1 = profile1.dealBreakers || [];
  const db2 = profile2.dealBreakers || [];
  
  // On ne peut pas vraiment calculer sans connaître les traits de l'autre
  // Mais on donne des points si peu de deal-breakers
  const avgDealBreakers = (db1.length + db2.length) / 2;
  if (avgDealBreakers <= 3) {
    score += 10;
  } else if (avgDealBreakers <= 5) {
    score += 7;
  } else {
    score += 4;
  }

  // Calculer le pourcentage final
  const percentage = Math.round((score / maxScore) * 100);

  // Déterminer le badge
  let badge = null;
  if (percentage >= 85 && insights.some(i => i.type === 'healing')) {
    badge = { id: 'soul_healer', name: 'Âme Guérisseuse', emoji: '💜' };
  } else if (percentage >= 80 && insights.some(i => i.type === 'mirror')) {
    badge = { id: 'soul_mirror', name: 'Miroir d\'Âme', emoji: '🪞' };
  } else if (percentage >= 90) {
    badge = { id: 'twin_flame', name: 'Flamme Jumelle', emoji: '🔥' };
  }

  return {
    score: percentage,
    maxScore: 100,
    insights,
    badge,
    details: {
      attachment: att1 && att2 ? { score: Math.round((score / maxScore) * 30), max: 30 } : null,
      wounds: wound1 && wound2 ? { score: Math.round((score / maxScore) * 25), max: 25 } : null,
      shadow: shadow1 && shadow2 ? { score: Math.round((score / maxScore) * 20), max: 20 } : null,
      intensity: int1 && int2 ? { score: Math.round((score / maxScore) * 15), max: 15 } : null,
      dealBreakers: { score: Math.round((score / maxScore) * 10), max: 10 }
    }
  };
};

// ============================================
// 🎯 COMPOSANT QUIZ NYX 2.0
// ============================================

export const Nyx2Quiz = ({ 
  onComplete, 
  onClose, 
  existingProfile = {},
  isDarkMode = true,
  displayName = ''
}) => {
  const [currentSection, setCurrentSection] = useState('intro'); // intro, attachment, wounds, shadow, intensity, dealbreakers, results
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({
    attachment: {},
    wounds: {},
    shadow: {},
    intensity: {}
  });
  const [selectedDealBreakers, setSelectedDealBreakers] = useState([]);
  const [results, setResults] = useState(null);

  // Questions par section
  const sectionQuestions = {
    attachment: ATTACHMENT_QUESTIONS,
    wounds: WOUND_QUESTIONS,
    shadow: SHADOW_QUESTIONS,
    intensity: INTENSITY_QUESTIONS
  };

  const sectionOrder = ['attachment', 'wounds', 'shadow', 'intensity', 'dealbreakers'];
  const sectionTitles = {
    attachment: { title: 'Style d\'Attachement', emoji: '🔗', description: 'Comment tu te lies aux autres' },
    wounds: { title: 'Blessure Profonde', emoji: '🩹', description: 'Ce qui te fait mal au plus profond' },
    shadow: { title: 'Shadow Self', emoji: '🎭', description: 'Ce que tu refuses de voir en toi' },
    intensity: { title: 'Intensité Émotionnelle', emoji: '🌊', description: 'Comment tu vis l\'amour' },
    dealbreakers: { title: 'Deal-Breakers', emoji: '🚫', description: 'Tes limites non-négociables' }
  };

  const handleAnswer = (questionId, optionId, scores) => {
    const newAnswers = { ...answers };
    newAnswers[currentSection] = {
      ...newAnswers[currentSection],
      [questionId]: { optionId, scores }
    };
    setAnswers(newAnswers);

    // Passer à la question suivante ou à la section suivante
    const questions = sectionQuestions[currentSection];
    if (questions && currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      // Section terminée, passer à la suivante
      const currentIndex = sectionOrder.indexOf(currentSection);
      if (currentIndex < sectionOrder.length - 1) {
        setTimeout(() => {
          setCurrentSection(sectionOrder[currentIndex + 1]);
          setCurrentQuestion(0);
        }, 500);
      }
    }
  };

  const calculateResults = () => {
    // Calculer les résultats pour chaque dimension
    const results = {};

    // Attachment Style
    const attScores = { secure: 0, anxious: 0, avoidant: 0, disorganized: 0 };
    Object.values(answers.attachment).forEach(a => {
      Object.entries(a.scores || {}).forEach(([key, val]) => {
        attScores[key] = (attScores[key] || 0) + val;
      });
    });
    results.attachmentStyle = Object.entries(attScores).sort((a, b) => b[1] - a[1])[0][0];

    // Dominant Wound
    const woundScores = { rejection: 0, abandonment: 0, humiliation: 0, betrayal: 0, injustice: 0 };
    Object.values(answers.wounds).forEach(a => {
      Object.entries(a.scores || {}).forEach(([key, val]) => {
        woundScores[key] = (woundScores[key] || 0) + val;
      });
    });
    results.dominantWound = Object.entries(woundScores).sort((a, b) => b[1] - a[1])[0][0];

    // Shadow Trait
    const shadowScores = { chaos: 0, control: 0, weakness: 0, arrogance: 0, neediness: 0, coldness: 0 };
    Object.values(answers.shadow).forEach(a => {
      Object.entries(a.scores || {}).forEach(([key, val]) => {
        shadowScores[key] = (shadowScores[key] || 0) + val;
      });
    });
    results.shadowTrait = Object.entries(shadowScores).sort((a, b) => b[1] - a[1])[0][0];

    // Emotional Intensity
    const intScores = { calm: 0, moderate: 0, intense: 0, chaotic: 0 };
    Object.values(answers.intensity).forEach(a => {
      Object.entries(a.scores || {}).forEach(([key, val]) => {
        intScores[key] = (intScores[key] || 0) + val;
      });
    });
    results.emotionalIntensity = Object.entries(intScores).sort((a, b) => b[1] - a[1])[0][0];

    // Deal Breakers
    results.dealBreakers = selectedDealBreakers;

    setResults(results);
    setCurrentSection('results');

    // Confetti !
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#8b5cf6', '#6366f1', '#a855f7']
    });
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete({
        ...results,
        completedAt: new Date().toISOString()
      });
    }
  };

  // Render section intro
  if (currentSection === 'intro') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`p-6 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
      >
        <motion.div
          animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="text-6xl mb-4"
        >
          🌙
        </motion.div>
        <h2 className="text-2xl font-bold mb-2">Nyx 2.0</h2>
        <p className={`text-lg mb-4 ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
          Les Zones d'Ombre Profondes
        </p>
        <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Au-delà des péchés capitaux... découvre les vraies profondeurs de ton âme.
        </p>

        <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
            ✨ Ce quiz explore 5 dimensions secrètes de ta personnalité pour des matchs vraiment profonds.
          </p>
        </div>

        <div className="space-y-2 mb-6 text-left">
          {sectionOrder.map((section, idx) => (
            <div 
              key={section}
              className={`flex items-center gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}
            >
              <span className="text-xl">{sectionTitles[section].emoji}</span>
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {sectionTitles[section].title}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {sectionTitles[section].description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={() => setCurrentSection('attachment')}
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-6 text-lg"
        >
          <Moon className="w-5 h-5 mr-2" />
          Plonger dans l'ombre 🌙
        </Button>
      </motion.div>
    );
  }

  // Render deal breakers section
  if (currentSection === 'dealbreakers') {
    return (
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        className={`p-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
      >
        <div className="text-center mb-6">
          <span className="text-4xl">🚫</span>
          <h3 className="text-xl font-bold mt-2">Tes Deal-Breakers</h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Sélectionne jusqu'à 5 critères non-négociables
          </p>
        </div>

        <div className="grid grid-cols-1 gap-2 mb-6 max-h-[400px] overflow-y-auto">
          {DEAL_BREAKERS.map((db) => {
            const isSelected = selectedDealBreakers.includes(db.id);
            const isDisabled = !isSelected && selectedDealBreakers.length >= 5;
            
            return (
              <button
                key={db.id}
                onClick={() => {
                  if (isSelected) {
                    setSelectedDealBreakers(selectedDealBreakers.filter(id => id !== db.id));
                  } else if (selectedDealBreakers.length < 5) {
                    setSelectedDealBreakers([...selectedDealBreakers, db.id]);
                  }
                }}
                disabled={isDisabled}
                className={`p-3 rounded-xl text-left transition-all ${
                  isSelected 
                    ? 'bg-red-500/20 border-2 border-red-500' 
                    : isDisabled
                      ? 'opacity-50 cursor-not-allowed bg-gray-800/30'
                      : isDarkMode ? 'bg-gray-800/50 hover:bg-gray-700/50' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{db.text}</span>
                  {isSelected && <X className="w-4 h-4 text-red-500" />}
                </div>
              </button>
            );
          })}
        </div>

        <p className={`text-center text-sm mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {selectedDealBreakers.length}/5 sélectionnés
        </p>

        <Button
          onClick={calculateResults}
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
        >
          Voir mes résultats 🔮
        </Button>
      </motion.div>
    );
  }

  // Render results
  if (currentSection === 'results' && results) {
    const attachment = ATTACHMENT_STYLES[results.attachmentStyle];
    const wound = FIVE_WOUNDS[results.dominantWound];
    const shadow = SHADOW_TRAITS[results.shadowTrait];
    const intensity = EMOTIONAL_INTENSITY[results.emotionalIntensity];

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`p-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
      >
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', bounce: 0.5 }}
            className="text-6xl mb-2"
          >
            🌙
          </motion.div>
          <h2 className="text-2xl font-bold">Ton Profil d'Ombre</h2>
          <p className={isDarkMode ? 'text-purple-300' : 'text-purple-600'}>
            Les profondeurs de ton âme révélées
          </p>
        </div>

        <div className="space-y-4 mb-6">
          {/* Attachment */}
          <div className={`p-4 rounded-xl bg-gradient-to-r ${attachment.color} bg-opacity-20`}>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{attachment.emoji}</span>
              <div>
                <p className="font-bold text-white">{attachment.name}</p>
                <p className="text-xs text-white/70">Style d'attachement</p>
              </div>
            </div>
            <p className="text-sm text-white/90">{attachment.description}</p>
          </div>

          {/* Wound */}
          <div className={`p-4 rounded-xl bg-gradient-to-r ${wound.color} bg-opacity-20`}>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{wound.emoji}</span>
              <div>
                <p className="font-bold text-white">{wound.name}</p>
                <p className="text-xs text-white/70">Blessure dominante</p>
              </div>
            </div>
            <p className="text-sm text-white/90">{wound.description}</p>
          </div>

          {/* Shadow */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{shadow.emoji}</span>
              <div>
                <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{shadow.name}</p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Shadow Self</p>
              </div>
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {shadow.hiddenSelf}
            </p>
          </div>

          {/* Intensity */}
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{intensity.emoji}</span>
              <div>
                <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{intensity.name}</p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Intensité émotionnelle</p>
              </div>
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {intensity.inLove}
            </p>
          </div>
        </div>

        <Button
          onClick={handleComplete}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-6 text-lg"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Sauvegarder mon profil d'ombre 💜
        </Button>
      </motion.div>
    );
  }

  // Render questions
  const questions = sectionQuestions[currentSection];
  const question = questions?.[currentQuestion];
  const sectionInfo = sectionTitles[currentSection];

  if (!question) return null;

  return (
    <motion.div
      key={`${currentSection}-${currentQuestion}`}
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className={`p-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
    >
      {/* Section header */}
      <div className="text-center mb-6">
        <span className="text-3xl">{sectionInfo.emoji}</span>
        <h3 className="text-lg font-bold mt-2">{sectionInfo.title}</h3>
        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Question {currentQuestion + 1}/{questions.length}
        </p>
      </div>

      {/* Progress bar */}
      <div className={`h-2 rounded-full mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
        />
      </div>

      {/* Question */}
      <p className={`text-lg mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        {question.question}
      </p>

      {/* Options */}
      <div className="space-y-3">
        {question.options.map((option) => (
          <motion.button
            key={option.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleAnswer(question.id, option.id, option.score)}
            className={`w-full p-4 rounded-xl text-left transition-all ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-purple-900/50 border border-gray-700 hover:border-purple-500' 
                : 'bg-gray-100 hover:bg-purple-100 border border-gray-200 hover:border-purple-400'
            }`}
          >
            {option.text}
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
};

export default Nyx2Quiz;
